/**
 * Temporary live debug logging for first-login OAuth investigation with real customers.
 * TODO(auth-live-debug): delete this file and remove all authLiveDebug() imports/calls when done.
 *
 * Client-side only — never log tokens, OAuth codes, URLs with query/hash, invite tokens,
 * full emails, userIds, or accountIds (use server NR Logs / Supabase for those).
 */
/* eslint-disable no-console -- TODO(auth-live-debug): remove live customer auth debug logs */

type AuthLiveDebugPayload = Record<string, unknown>

const REDACTED = '[REDACTED]'

const SENSITIVE_KEY =
  /^(access[_-]?token|refresh[_-]?token|authorization|password|secret|invite[_-]?token|code|search|hash|url|error[_-]?description)$/i

/** PII kept off the client — strip even if callers spread server-shaped fields. */
const CLIENT_PII_KEYS = new Set(['userId', 'email', 'accountId'])

/** Safe correlation fields for browser console during auth investigation. */
const SAFE_AUTH_OBSERVABILITY_KEYS = new Set([
  'userIdPrefix',
  'emailDomain',
  'displayName',
  'authAttemptId',
])

function redactEmail(value: string): string {
  const at = value.indexOf('@')
  if (at <= 0) return REDACTED
  return `***${value.slice(at)}`
}

function redactString(value: string, key: string): string {
  if (SENSITIVE_KEY.test(key)) return REDACTED
  if (CLIENT_PII_KEYS.has(key)) return REDACTED
  if (value.includes('access_token=') || value.includes('refresh_token=') || value.includes('code=')) {
    return REDACTED
  }
  if (key.toLowerCase().includes('email') || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return redactEmail(value)
  }
  return value
}

function sanitizePayload(payload: AuthLiveDebugPayload): AuthLiveDebugPayload {
  const out: AuthLiveDebugPayload = {}
  for (const [key, raw] of Object.entries(payload)) {
    if (CLIENT_PII_KEYS.has(key)) {
      continue
    }
    if (raw === null || raw === undefined) {
      out[key] = raw
      continue
    }
    if (typeof raw === 'string') {
      if (SAFE_AUTH_OBSERVABILITY_KEYS.has(key)) {
        out[key] = raw
        continue
      }
      out[key] = redactString(raw, key)
      continue
    }
    if (typeof raw === 'boolean' || typeof raw === 'number') {
      out[key] = raw
      continue
    }
    out[key] = REDACTED
  }
  return out
}

export function authLiveDebug(step: string, payload?: AuthLiveDebugPayload): void {
  const ts = new Date().toISOString()
  const elapsedMs =
    typeof performance !== 'undefined' ? Math.round(performance.now()) : Date.now()
  if (payload !== undefined) {
    console.log(`[AUTH-LIVE-DEBUG ${ts} +${elapsedMs}ms] ${step}`, sanitizePayload(payload))
    return
  }
  console.log(`[AUTH-LIVE-DEBUG ${ts} +${elapsedMs}ms] ${step}`)
}
